<script>
import {defineComponent, ref, onBeforeMount, nextTick, reactive, computed} from "vue";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import Column from "primevue/column";
import {useApiCall} from "@/composables/useApiCall";
import {
  geoRestrictionsApiCall,
  createGeorRestrictionsApiCall,
  updateGeorRestrictionsApiCall,
  deleteGeorRestrictionsApiCall
} from "@/services/yandex";
import {useI18n} from "vue-i18n";
import InputText from "primevue/inputtext";
import Checkbox from "primevue/checkbox";
import Calendar from "primevue/calendar";
import Dropdown from "primevue/dropdown";

export default defineComponent({
  layout: { name: 'AdminLayout' },
  components: {Dropdown, Calendar, Checkbox, Column, ConfirmationModal, DataTable, Button, InputText},
  setup() {
    const { t } = useI18n()
    const visibleDeleteModal = ref(false)
    const visibleCreateModal = ref(false)
    const selectGeo = ref(null)
    const deleteId = ref(0)
    const fields = reactive({
      name: '',
      lat: '',
      lon: '',
      radius: '',
    })
    const isValid = computed(() => fields.name && fields.lat && fields.lon && fields.radius)
    const geoRestrictions = ref([])

    const toggleDeleteModal = (id = 0) => {
      deleteError.value = null
      visibleDeleteModal.value = !visibleDeleteModal.value
      if (id) {
        deleteId.value = id
      } else {
        deleteId.value = 0
      }
    }

    const toggleCreateModal = (geo) => {
      if (geo) {
        selectGeo.value = geo
        fields.lat = +geo.geo?.center[1]
        fields.name = geo.name
        fields.lon = +geo.geo?.center[0]
        fields.radius = geo.geo?.radius
      }
      visibleCreateModal.value = !visibleCreateModal.value
      if (!visibleCreateModal.value) {
        fields.lat = ''
        fields.name = ''
        fields.lon = ''
        fields.radius = ''
        createError.value = null
        selectGeo.value = null
      }
    }

    const {
      isLoading: isGeoLoading,
      data: geoData,
      error: geoError,
      executeApiCall: getGeoAction,
    } = useApiCall(geoRestrictionsApiCall, true)

    const {
      isLoading: isCreateLoading,
      data: createData,
      error: createError,
      executeApiCall: createAction,
    } = useApiCall(createGeorRestrictionsApiCall, true)

    const {
      isLoading: isDeleteLoading,
      data: deleteData,
      error: deleteError,
      executeApiCall: deleteAction,
    } = useApiCall(deleteGeorRestrictionsApiCall, true)

    const {
      isLoading: isUpdateLoading,
      data: updateData,
      error: updateError,
      executeApiCall: updateAction,
    } = useApiCall(updateGeorRestrictionsApiCall, true)

    const loadGeos = async () => {
      try {
        await getGeoAction()
        if (geoData.value) {
          geoRestrictions.value = [ ...geoData.value.data.items ]
        }
      } catch {
        console.error(geoError.value )
      }
    }

    const deleteItem = async () => {
      try {
        await deleteAction({id: deleteId.value})
        if (deleteData.value) {
          geoRestrictions.value = geoRestrictions.value.filter(geo => geo.id !== deleteId.value)
          toggleDeleteModal(0)
        }
      } catch {
        console.error(deleteError.value)
      }
    }

    const errorForm = ref(null)
    const createItem = async () => {
      errorForm.value = null
      try {
        if (selectGeo.value) {
          await updateAction({id: selectGeo.value.id, data: {
              name: fields.name,
              lat: Number(fields.lat),
              lon: Number(fields.lon),
              radius: Number(fields.radius),
            }})
          if (updateData.value) {
            await loadGeos()
            toggleCreateModal()
          }
        } else {
          await createAction({
            name: fields.name,
            lat: Number(fields.lat),
            lon: Number(fields.lon),
            radius: Number(fields.radius),
          })
          if (createData.value) {
            await loadGeos()
            toggleCreateModal()
          }
        }
      } catch {
        if (createError.value?.data) {
          errorForm.value = {...createError.value.data}
        }
        if (updateError.value?.data) {
          errorForm.value = {...updateError.value.data}
        }
      }
    }

    onBeforeMount(async () => {
      await loadGeos()
      await nextTick(() => {
        window.scrollTo(0, 0)
      })
    })

    return {
      t,
      deleteItem,
      createItem,
      toggleCreateModal,
      isUpdateLoading,
      isGeoLoading,
      isCreateLoading,
      isDeleteLoading,
      visibleDeleteModal,
      visibleCreateModal,
      errorForm,
      deleteId,
      toggleDeleteModal,
      fields,
      isValid,
      geoRestrictions,
      createError,
      selectGeo,
    };
  }
});
</script>

<template>
  <ConfirmationModal v-if="visibleDeleteModal" v-model:visible="visibleDeleteModal">
    <template #header>
      <span>Удалить зону</span>
    </template>

    <template #default>
      <span class="flex mt-2">Вы уверены, что хотите удалить зону?</span>
    </template>

    <template #footer>
      <div class="flex justify-content-between mt-4">
        <Button :label="t('labels.cancel')" @click="toggleDeleteModal(0)"
                class="btn-primary-outlined font-light w-12"/>
        <Button :loading="isDeleteLoading" :disabled="isDeleteLoading" :label="t('labels.destroy')" @click="deleteItem"
                class="btn-primary font-light ml-3 w-12"/>
      </div>
    </template>
  </ConfirmationModal>
  <ConfirmationModal v-model:visible="visibleCreateModal" closable @close-modal="toggleCreateModal" class="geo__popup">
    <template #header>
      {{ selectGeo ? 'Редактировать' : 'Создать' }} зону
    </template>

    <template #default>
      <div class="geo__popup-form">
        <div class="geo__popup-grid">
          <div class="geo__popup-wrapper">
          <span class="p-float-label w-full">
             <InputText
                 v-model="fields.name"
                 id="name_ru"
                 class="w-full"
             />
           <label for="name_ru">Название зоны</label>
          </span>
            <span v-if="errorForm?.name" class="geo__popup-error color-red">{{errorForm?.name[0] || ''}}</span>
          </div>
          <div class="geo__popup-wrapper">
          <span class="p-float-label w-full">
             <InputText
                 v-model="fields.radius"
                 id="name_ru"
                 class="w-full"
                 type="number"
             />
           <label for="name_ru">Радиус зоны (м)</label>
          </span>
            <span v-if="errorForm?.radius" class="geo__popup-error color-red">{{errorForm?.radius[0] || ''}}</span>
          </div>
        </div>
        <div class="geo__popup-grid">
          <div class="geo__popup-wrapper">
            <span class="p-float-label w-full">
               <InputText
                   v-model="fields.lon"
                   id="lon"
                   class="w-full"
                   type="number"
               />
             <label for="lon">Широта зоны</label>
            </span>
            <span v-if="errorForm?.lon" class="geo__popup-error color-red">{{errorForm?.lon[0] || ''}}</span>
          </div>
          <div class="geo__popup-wrapper">
            <span class="p-float-label w-full">
               <InputText
                   v-model="fields.lat"
                   id="lat"
                   class="w-full"
                   type="number"
               />
             <label for="lat">Долгота зоны</label>
            </span>
            <span v-if="errorForm?.lat" class="geo__popup-error color-red">{{errorForm?.lat[0] || ''}}</span>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex flex-column gap-3">
        <span v-if="createError && !createError.data" class="geo__popup-error color-red">{{ createError.error }}</span>
        <Button :loading="isCreateLoading || isUpdateLoading" :disabled="isCreateLoading || isUpdateLoading || !isValid" label="Сохранить" @click="createItem"
                class="btn-primary geo__header-btn font-light"/>
      </div>
    </template>
  </ConfirmationModal>
  <section class="geo__header">
      <h1 class="geo__header-title">Географические ограничения</h1>
      <div class="flex gap-3">
        <Button label="Создать зону" @click="toggleCreateModal(null)" class="btn-primary geo__header-btn"/>
      </div>
  </section>

  <section class="geo__body">
    <div class="geo__body-table">
      <DataTable :value="geoRestrictions" :loading="isGeoLoading" showGridlines tableStyle="min-width: 50rem" selectionMode="single">
        <Column field="id" header="Идентификатор зоны">
          <template #body="slotProps">
            <button class="geo__body-table-name">
              <span>{{ slotProps.data.id }}</span>
            </button>
          </template>
        </Column>
        <Column field="name" header="Название">
          <template #body="slotProps">
            <div class="geo__body-table-name">
              {{  slotProps.data.name }}
            </div>
          </template>
        </Column>
        <Column field="center" header="Широта центра">
          <template #body="slotProps">
            <div class="geo__body-table-name">
              {{ slotProps.data?.geo?.center[0] }}
            </div>
          </template>
        </Column>
        <Column field="center" header="Долгота центра">
          <template #body="slotProps">
            <div class="geo__body-table-name">
              {{ slotProps.data?.geo?.center[1]}}
            </div>
          </template>
        </Column>
        <Column field="radius" header="Радиус">
          <template #body="slotProps">
            <div class="geo__body-table-name">
              {{ slotProps.data?.geo.radius }} м
            </div>
          </template>
        </Column>
        <Column field="text" header="Действия">
          <template #body="slotProps">
            <div class="geo__body-table-actions">
              <button  @click="toggleCreateModal(slotProps.data)" class="geo__body-table-delete">
                <img src="@/assets/images/icons/Edit.svg" width="24" alt="edit-icon"/>
              </button>
              <button  @click="toggleDeleteModal(slotProps.data.id)" class="geo__body-table-delete">
                <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.2679 5.36049H14.7322C14.7322 4.76849 14.497 4.20074 14.0784 3.78213C13.6598 3.36352 13.092 3.12835 12.5 3.12835C11.908 3.12835 11.3403 3.36352 10.9217 3.78213C10.503 4.20074 10.2679 4.76849 10.2679 5.36049ZM8.92859 5.36049C8.92859 4.41329 9.30486 3.50488 9.97464 2.83511C10.6444 2.16534 11.5528 1.78906 12.5 1.78906C13.4472 1.78906 14.3556 2.16534 15.0254 2.83511C15.6952 3.50488 16.0714 4.41329 16.0714 5.36049H21.6518C21.8294 5.36049 21.9997 5.43104 22.1253 5.55662C22.2509 5.68221 22.3214 5.85253 22.3214 6.03013C22.3214 6.20773 22.2509 6.37806 22.1253 6.50364C21.9997 6.62923 21.8294 6.69978 21.6518 6.69978H20.4822L19.3956 19.7382C19.3165 20.6867 18.8839 21.5708 18.1835 22.2153C17.4831 22.8598 16.5661 23.2176 15.6143 23.2176H9.38573C8.43394 23.2176 7.51693 22.8598 6.81654 22.2153C6.11615 21.5708 5.68354 20.6867 5.60448 19.7382L4.51787 6.69978H3.34823C3.17063 6.69978 3.00031 6.62923 2.87472 6.50364C2.74914 6.37806 2.67859 6.20773 2.67859 6.03013C2.67859 5.85253 2.74914 5.68221 2.87472 5.55662C3.00031 5.43104 3.17063 5.36049 3.34823 5.36049H8.92859ZM6.9393 19.6266C6.99037 20.2403 7.27022 20.8125 7.72336 21.2296C8.17651 21.6467 8.76985 21.8782 9.38573 21.8783H15.6143C16.2302 21.8782 16.8235 21.6467 17.2767 21.2296C17.7298 20.8125 18.0097 20.2403 18.0607 19.6266L19.1393 6.69978H5.86162L6.9393 19.6266ZM10.4911 9.82478C10.6687 9.82478 10.839 9.89533 10.9646 10.0209C11.0902 10.1465 11.1607 10.3168 11.1607 10.4944V18.0837C11.1607 18.2613 11.0902 18.4316 10.9646 18.5572C10.839 18.6828 10.6687 18.7533 10.4911 18.7533C10.3135 18.7533 10.1432 18.6828 10.0176 18.5572C9.892 18.4316 9.82145 18.2613 9.82145 18.0837V10.4944C9.82145 10.3168 9.892 10.1465 10.0176 10.0209C10.1432 9.89533 10.3135 9.82478 10.4911 9.82478ZM15.1786 10.4944C15.1786 10.3168 15.108 10.1465 14.9825 10.0209C14.8569 9.89533 14.6865 9.82478 14.5089 9.82478C14.3313 9.82478 14.161 9.89533 14.0354 10.0209C13.9099 10.1465 13.8393 10.3168 13.8393 10.4944V18.0837C13.8393 18.2613 13.9099 18.4316 14.0354 18.5572C14.161 18.6828 14.3313 18.7533 14.5089 18.7533C14.6865 18.7533 14.8569 18.6828 14.9825 18.5572C15.108 18.4316 15.1786 18.2613 15.1786 18.0837V10.4944Z" fill="#E26B6B"/>
                </svg>
              </button>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </section>
</template>

<style lang="scss">
th:nth-child(3),
th:nth-child(4) {
  max-width: 200px;
}
.geo__header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 20px;
  padding-top: 14px;
  margin-bottom: 24px;
}
.geo__header-btn {
  font-weight: 400;
  font-size: 13px;
  line-height: 22px;
  min-width: 228px;
  height: 46px;
  color: white;
  background: #C3987A;
}
.geo__body-table {
  .p-column-header-content  {
    justify-content: start !important;
    .p-column-title {
      font-weight: 600;
      font-size: 18px;
      line-height: 100%;
      text-align: start;
      color: #333333;
    }
  }
}

.geo__body-table-name,
.geo__body-table-permissions {
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;
  text-align: left;
  color: #333333;
}
.geo__body-table-name {
  display: flex;
  justify-content: start;
  text-decoration: unset;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0 4px;
}
.geo__body-table-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}
.geo__body-table-actions:before {
  position: absolute;
  content: '';
  width: 1px;
  height: 25px;
  background: #E1E8ED;
  top: 50%;
  left: calc(50% - 0.5px);
  transform: translateY(-50%);
  z-index: 2;
}
.geo__body-table-delete,
.geo__body-table-edit {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
}
.geo__popup-wrapper {
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 10px;
  width: 100%;
}
.geo__popup-error {
  font-size: 10px;
}
.geo__popup {
  max-width: unset !important;
  .p-dialog-header-icons {
    position: absolute;
    right: 8px;
    top: 8px;
  }
  .p-dialog-header {
    padding: 20px 20px 0 20px !important;
  }
  .p-dialog-content {
    padding: 0 20px 20px;
  }
  .p-dialog-footer {
    padding: 0 20px 20px;
    justify-content: start;
    text-align: start !important;
  }
  &.--list {
    max-width: 940px !important;
    width: 100%;

    .p-dialog-content {
      padding: 0 !important;
    }
    .p-dialog-footer {
      padding-top: 20px !important;
    }
    label {
      font-weight: 400;
      font-size: 18px;
      line-height: 100%;
      color: #333333;
      cursor: pointer;
    }
  }
}
.geo__popup-content {
  display: grid;
  gap: 1px;
  background: #E1E8ED;
  grid-template-columns: repeat(3, 1fr);
  border-top: 1px solid #E1E8ED;
  border-bottom: 1px solid #E1E8ED;
}
.geo__popup-content-item {
  padding: 16px 20px;
  background: white;
}
.geo__popup-content-error {
  padding: 16px 20px;
}
.geo__popup-form {
  width: 500px;
  padding: 25px 0 0;
  display: flex;
  flex-direction: column;
  gap: 25px;
}
.geo__popup-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  .geo__popup-wrapper {
    max-width: calc(50% - 10px);
  }
}
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>